import { supabase } from '../lib/supabase.js';
import { openLightbox } from '../lib/lightbox.js';
import { getMediaType, mediaThumbHTML, ACCEPT_ALL_MEDIA } from '../lib/media.js';
import { uploadEventPhoto, uploadFiles } from '../lib/photos.js';
import { willCompressVideo } from '../lib/video-compress.js';

function esc(s) { if (s == null) return ''; return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
function fmtDate(d) { const dt = new Date(d); return `${dt.getDate()}. ${dt.getMonth() + 1}. ${dt.getFullYear()}`; }

const BUCKET = 'photos-event';

async function getThumbUrl(path) {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
    if (error) return null;
    return data?.signedUrl;
}

export async function renderGalerie(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';

    const { data: events, error } = await supabase
        .from('events')
        .select(`
            *,
            place:places(name),
            event_photos:photos!event_id(*)
        `)
        .eq('type', 'gig')
        .order('event_date', { ascending: false });

    if (error) {
        container.innerHTML = `<div class="state-banner error">Chyba: ${esc(error.message)}</div>`;
        return;
    }

    if (!events || events.length === 0) {
        container.innerHTML = '<div class="state-banner">Žádné akce.</div>';
        return;
    }

    // Generate thumbnail URLs (signed) for each photo in parallel
    for (const ev of events) {
        const photos = (ev.event_photos || []).filter(p => p.type === 'event');
        await Promise.all(photos.map(async p => { p.url = await getThumbUrl(p.storage_path); }));
        ev._photos = photos;
    }

    // Filtruj jen akce s alespoň jednou fotkou/médiem.
    // Akce bez médií se zobrazují v Přehledu / Historii — kde tam fotku přidáš.
    const eventsWithPhotos = events.filter(ev => ev._photos.length > 0);

    if (eventsWithPhotos.length === 0) {
        container.innerHTML = `
            <div class="state-banner">
                V galerii zatím nejsou žádné fotky. Nahraj je v záložce
                <strong>Přehled</strong> (nadcházející akce) nebo <strong>Historie</strong> (proběhlé akce)
                přes tlačítko <em>Foto z akce</em>.
            </div>
        `;
        return;
    }

    container.innerHTML = `
        <div class="filters">
            <input class="search-input" type="search" placeholder="Hledat akci...">
        </div>

        <div class="gallery-grid">
            ${eventsWithPhotos.map(ev => `
                <div class="venue-card" data-event="${ev.id}">
                    <h3>${fmtDate(ev.event_date)} · ${esc(ev.place?.name || '—')}</h3>
                    <div class="photo-thumbs">
                        ${ev._photos.map(p => {
                            if (!p.url) return `<div class="thumb">…</div>`;
                            const type = getMediaType(p.storage_path);
                            return mediaThumbHTML({
                                url: p.url, path: p.storage_path, mediaType: type,
                                dataAttrs: `data-photo="${p.id}" data-path="${esc(p.storage_path)}"`
                            });
                        }).join('')}
                        <label class="thumb add" title="Nahrát foto, video nebo audio">
                            +
                            <input type="file" accept="${ACCEPT_ALL_MEDIA}" multiple style="display:none;" data-upload="${ev.id}">
                        </label>
                    </div>
                </div>
            `).join('')}
        </div>

        <div class="footer-note">
            <strong>Galerie akcí:</strong> Pouze akce, které mají alespoň jednu fotku / video / audio.
            Nahrávat nové fotky můžeš tady (přes tlačítko <em>+</em>) nebo v záložkách <strong>Přehled</strong> a <strong>Historie</strong>.
            Pro fotky <em>místa</em> (zdroj 400 V, parking) jdi do záložky <strong>Místa</strong>.
        </div>
    `;

    // Upload handlers
    container.querySelectorAll('[data-upload]').forEach(input => {
        input.addEventListener('change', async () => {
            const eventId = input.dataset.upload;
            if (!input.files?.length) return;

            const card = input.closest('.venue-card');
            const progress = document.createElement('div');
            progress.className = 'upload-progress';
            progress.textContent = `Nahrávám…`;
            card.insertBefore(progress, card.querySelector('.actions'));

            const { ok, fail } = await uploadFiles(
                uploadEventPhoto, eventId, input.files,
                ({ ok, total }) => { progress.textContent = `Nahráno ${ok}/${total}…`; },
                (file) => {
                    if (willCompressVideo(file)) {
                        progress.textContent = 'Komprimuji video… (může chvíli trvat, nezavírej stránku)';
                    }
                }
            );
            if (fail) progress.textContent = `Hotovo: ${ok} ✓, ${fail} chyba`;
            renderGalerie(container);
        });
    });

    // Lightbox on thumbnail click — pole všech médií dané akce (pro navigaci)
    container.querySelectorAll('.venue-card').forEach(card => {
        const eventId = card.dataset.event;
        const ev = events.find(e => e.id === eventId);
        const photos = (ev?._photos || []).filter(p => p.url).map(p => ({
            url: p.url,
            filename: p.storage_path.split('/').pop(),
            bucket: BUCKET,
            path: p.storage_path,
            photoId: p.id
        }));
        card.querySelectorAll('.thumb[data-photo]').forEach((el, idx) => {
            el.style.cursor = 'pointer';
            el.addEventListener('click', () => {
                openLightbox({
                    photos, startIndex: idx,
                    onDelete: () => renderGalerie(container)
                });
            });
        });
    });
}
